import { yupResolver } from '@hookform/resolvers/yup'
import get from 'lodash-es/get'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { useShareAssetModalStore } from '@/lib/stores/shareAssetModalStore'
import { LuGitPullRequestArrow } from 'react-icons/lu'
import { FormattedMessage } from 'react-intl'

import { Button } from '@/lib/core/components/Button'
import { FeatureModal } from '@/lib/core/components/Modal'
import { SelectOption } from '@/lib/core/components/Select'
import { formsMessages } from '@/lib/core/messages'

import { useAllAssets } from '@/lib/features/Assets/api'
import { WarningBox } from '@/lib/features/Assets/components/AddAssetModal/AddAssetModal.styles'
import { ContactSelectModal } from '@/lib/features/SmartShares/components/ContactSelectModal'
import { SelectShareClassModal } from '@/lib/features/SmartShares/components/SelectShareClassModal'
import {
  Actions,
  Container,
  convertToPayload,
  getBlockedShareFlowReason,
  convertValidatePayload,
  collectShareFormErrors,
  getAssetsStepSubtitle,
  getAssetsStepTitle,
  RequiredFieldsLabel,
  shouldSelectShareClass,
} from '@/lib/features/SmartShares/components/ShareAssetModal'
import {
  AssetsTable,
  SelectContact,
} from '@/lib/features/SmartShares/components/ShareAssetModal/components'
import { REFERENCE_MAX_LENGTH } from '@/lib/features/SmartShares/constants'
import { PostSharesResponseError, Share } from '@/lib/features/SmartShares/interfaces'

import { FeatureTypes } from '@/lib/utils/features'
import { setFormErrors } from '@/lib/utils/forms'

import { useShareAssets, useSmartShareValidation, useUpdateShareAssets } from '../../api'
import { useDateRangeStore } from '../../store'
import messages from './ShareAssetModal.messages'
import styles from './ShareAssetModal.module.css'
import { AssetRowFormState, ShareFormState } from './ShareAssetModal.types'

const assetDefaultValues: AssetRowFormState = {
  asset: null,
  durationType: 'instant',
  reference: '',
}

export type ShareAssetModalProps = {
  initialOptions?: Array<SelectOption>
  isActive: boolean
  onAddNewAsset?: () => void
  shareToExtend?: Share
  onwardShareToExtend?: Share
  onClose?: (withSuccess?: boolean) => void
}

export const ShareAssetModal = ({
  initialOptions = [],
  isActive,
  onAddNewAsset,
  onClose,
  shareToExtend,
  onwardShareToExtend,
}: ShareAssetModalProps) => {
  // Get state from global store
  const {
    isOpen: storeIsOpen,
    initialOptions: storeInitialOptions,
    shareToExtend: storeShareToExtend,
    onwardShareToExtend: storeOnwardShareToExtend,
    onSuccessCallback,
    onAddNewAssetCallback,
    shareClass,
    setShareClass,
    closeModal,
    setAddedShare: setStoreAddedShare,
    setShowSharesSummaryModal: setStoreShowSharesSummaryModal,
  } = useShareAssetModalStore()

  const { clearDateRange } = useDateRangeStore()

  // Use store values if available, otherwise fall back to props
  const effectiveIsActive = storeIsOpen || isActive
  const effectiveInitialOptions =
    storeInitialOptions.length > 0 ? storeInitialOptions : initialOptions
  const effectiveShareToExtend = storeShareToExtend || shareToExtend
  const effectiveOnwardShareToExtend = storeOnwardShareToExtend || onwardShareToExtend
  const effectiveOnAddNewAsset = onAddNewAssetCallback || onAddNewAsset

  const effectiveOnClose = (withSuccess?: boolean) => {
    // Call store success callback if available
    if (onSuccessCallback && withSuccess) {
      onSuccessCallback()
    }

    // cleanup date range store for the date range limitation
    if (effectiveShareToExtend) {
      clearDateRange(effectiveShareToExtend.id)
    }
    // Call prop callback if available
    if (onClose) {
      onClose(withSuccess)
    }
    // Close the store modal
    closeModal()
  }

  const [generalError, setGeneralError] = useState('')
  const [alreadyWarned, setAlreadyWarned] = useState<boolean>(false)

  const [shareAssetValues, setShareAssetValues] = useState<ShareFormState>()
  const [showContactSelectModal, setShowContactSelectModal] = useState<boolean>(false)
  const [warningAssets, setWarningAssets] = useState<number[]>([])

  const { trigger: validateSmartShare } = useSmartShareValidation()
  const { trigger: extendShareAsset } = useUpdateShareAssets()
  const { trigger: shareAssetsToContact } = useShareAssets()
  const searchAssets = useAllAssets()

  const shareFlowContext = useMemo(
    () => ({
      isExtend: !!effectiveShareToExtend,
      isOnwardShare: !!effectiveOnwardShareToExtend,
      shareClass,
    }),
    [effectiveShareToExtend, effectiveOnwardShareToExtend, shareClass],
  )

  const modalTitle = useMemo(() => getAssetsStepTitle(shareFlowContext), [shareFlowContext])

  const modalSubtitle = useMemo(() => getAssetsStepSubtitle(shareFlowContext), [shareFlowContext])

  const formSchema = useMemo(
    () =>
      yup.object().shape({
        assets: yup.array().of(
          yup.object({
            asset: yup
              .string()
              .nullable()
              .required(formsMessages.requiredValidation.defaultMessage),
            date: yup
              .object({
                from: yup.string().nullable(),
                to: yup.string().nullable(),
              })
              .nullable()
              .when('durationType', {
                is: 'custom',
                then: (schema) => schema.required(messages.datesRequiredValidation.defaultMessage),
              }),
            durationType: yup.string().nullable(),
            reference: yup
              .string()
              .max(REFERENCE_MAX_LENGTH, messages.referenceMaxLengthValidation.defaultMessage)
              .nullable(),
          }),
        ),
        organizationTo: yup
          .string()
          .nullable()
          .required(formsMessages.requiredValidation.defaultMessage),
        contactTo: yup.number().nullable().optional(),
        contactFrom: yup.number().nullable().optional(),
      }),
    [],
  )

  const formMethods = useForm<ShareFormState>({
    defaultValues: {
      assets: effectiveInitialOptions.length
        ? effectiveInitialOptions.map((option) => ({
            ...assetDefaultValues,
            asset: option.value as number,
          }))
        : effectiveShareToExtend || effectiveOnwardShareToExtend
          ? [
              {
                ...assetDefaultValues,
                asset: effectiveShareToExtend?.assetId || effectiveOnwardShareToExtend?.assetId,
              },
            ]
          : [{ ...assetDefaultValues }],
      organizationTo:
        effectiveShareToExtend?.organization?.id || effectiveOnwardShareToExtend?.organization?.id,
      contactTo:
        effectiveShareToExtend?.contactTo?.id ??
        effectiveOnwardShareToExtend?.contactTo?.id ??
        null,
      contactFrom:
        effectiveShareToExtend?.contactFrom?.id ??
        effectiveOnwardShareToExtend?.contactFrom?.id ??
        null,
    },
    resolver: yupResolver(formSchema) as unknown as Resolver<ShareFormState>,
  })

  const { formState, getValues, handleSubmit, reset, setError } = formMethods

  const { isSubmitted, isSubmitting, isValid } = formState

  // Everything wrong with the form, in reading order. `isValid` only settles once RHF
  // re-validates, so the list itself is what decides whether the summary is worth showing;
  // the generic message is the fallback for an invalid form with nothing enumerable.
  const formErrors = useMemo(
    () => collectShareFormErrors(formState.errors, generalError),
    [formState.errors, generalError],
  )
  const showErrorSummary = formErrors.length > 0 || (isSubmitted && !isValid)

  // Helper function to generate consistent keys for form re-rendering
  const getModalKey = (prefix: string = '') => {
    const shareId = effectiveShareToExtend?.id || 'new'
    const onwardId = effectiveOnwardShareToExtend?.id || 'none'
    return `${prefix}${effectiveIsActive}-${shareId}-${onwardId}`
  }

  // Reset form when modal opens with new data
  const prevIsActive = useRef(false)

  useEffect(() => {
    // Reset when modal transitions from closed to open
    if (effectiveIsActive && !prevIsActive.current) {
      const newDefaultValues = {
        assets: effectiveInitialOptions.length
          ? effectiveInitialOptions.map((option) => ({
              ...assetDefaultValues,
              asset: option.value as number,
            }))
          : effectiveShareToExtend || effectiveOnwardShareToExtend
            ? [
                {
                  ...assetDefaultValues,
                  asset: effectiveShareToExtend?.assetId || effectiveOnwardShareToExtend?.assetId,
                },
              ]
            : [{ ...assetDefaultValues }],
        organizationTo:
          effectiveShareToExtend?.organization?.id ||
          effectiveOnwardShareToExtend?.organization?.id,
        contactTo:
          effectiveShareToExtend?.contactTo?.id ??
          effectiveOnwardShareToExtend?.contactTo?.id ??
          null,
        contactFrom:
          effectiveShareToExtend?.contactFrom?.id ??
          effectiveOnwardShareToExtend?.contactFrom?.id ??
          null,
      }
      reset(newDefaultValues)
    }

    // Clear form when modal transitions from open to closed
    if (!effectiveIsActive && prevIsActive.current) {
      setStoreAddedShare(null)
      setGeneralError('')
      setAlreadyWarned(false)
      setWarningAssets([])
      setShareAssetValues(undefined)
      setShowContactSelectModal(false)
      setStoreShowSharesSummaryModal(false)
      reset()
    }

    prevIsActive.current = effectiveIsActive
  }, [effectiveIsActive]) // Simplified dependencies

  const onSubmit = async (values: ShareFormState) => {
    try {
      if (!warningAssets.length || alreadyWarned) {
        const payload = convertValidatePayload({
          assets: getValues('assets'),
          organizationTo: getValues('organizationTo'),
          shareToExtend: effectiveShareToExtend,
          shareClass,
          acceptWarning: alreadyWarned,
        })
        const response = await validateSmartShare(payload, { throwOnError: true })

        if (response?.isValid) {
          setShareAssetValues(values)
          setShowContactSelectModal(true)
        }
      } else {
        setShareAssetValues(values)
        setShowContactSelectModal(true)
      }
    } catch (err) {
      const errorResponse: Response = get(err, 'response')!
      const errorsJson = await errorResponse?.json()

      if (errorsJson?.errors) {
        const warnings = []
        for (const [index, err] of errorsJson.errors.entries()) {
          if (err !== 'None') {
            if (
              err ===
              'The share period of this Smart Share overlaps with that of an existing one for the same Asset and different Organization'
            ) {
              warnings.push(index + 1)
            } else {
              setError(`assets.${index}.date`, {
                message: err,
                type: 'apiError',
              })
            }
          }
        }

        if (warnings.length > 0) {
          setAlreadyWarned(true)
          setWarningAssets(warnings)
        }
      }
    }
  }

  const onShareSubmit = async (values: ShareFormState) => {
    if (Object.keys(formState.errors).length > 0) {
      return
    }

    // A delegation can be neither extended nor passed on. The table disables the action and the
    // row shows the reason, so reaching here means one of those was bypassed — say why rather
    // than letting the request go out and fail.
    const blockedReason = getBlockedShareFlowReason({
      shareToExtend: effectiveShareToExtend,
      onwardShareToExtend: effectiveOnwardShareToExtend,
    })
    if (blockedReason) {
      setGeneralError(blockedReason)
      setShowContactSelectModal(false)
      return
    }

    try {
      if (effectiveShareToExtend) {
        const shareId = effectiveShareToExtend.id
        const payload = convertToPayload({ ...values, shareToExtend: effectiveShareToExtend })

        const response = await extendShareAsset({
          params: { id: shareId },
          data: payload[0],
        })

        setStoreAddedShare([response])
      } else {
        const payload = convertToPayload({
          ...values,
          onwardShareToExtend: effectiveOnwardShareToExtend,
          shareClass,
        })
        const response = await shareAssetsToContact(payload)
        setStoreAddedShare(response)
      }
      setStoreShowSharesSummaryModal(true)
    } catch (err) {
      const apiErrors = err as PostSharesResponseError
      const generalApiError = get(apiErrors, 'errors[0]', '')
      const organizationToError = get(apiErrors, '0.organizationTo[0]')

      setGeneralError(generalApiError)
      setShowContactSelectModal(false)

      if (organizationToError) {
        setError('organizationTo', {
          message: organizationToError,
          type: 'apiError',
        })
      }

      setFormErrors(
        setError,
        { assets: apiErrors },
        (key: string) =>
          ({
            dateFrom: 'date',
            dateTo: 'date',
          })[key] || key,
      )
    }
  }

  useEffect(() => {
    if (isActive) {
      searchAssets.mutate()
    }
  }, [isActive])

  if (effectiveIsActive && shouldSelectShareClass(shareFlowContext)) {
    return <SelectShareClassModal isActive={effectiveIsActive} onClose={effectiveOnClose} />
  }

  if (showContactSelectModal) {
    return (
      <FormProvider {...formMethods}>
        <ContactSelectModal
          isActive={effectiveIsActive}
          shareToExtend={effectiveShareToExtend}
          onClose={effectiveOnClose}
          shareAssetSubmit={onShareSubmit}
          shareAssetValues={shareAssetValues}
          setShowContactSelectModal={setShowContactSelectModal}
          generalError={generalError}
          formMethods={formMethods}
          onwardShareToExtend={effectiveOnwardShareToExtend}
        />
      </FormProvider>
    )
  }

  return (
    <FeatureModal
      isActive={effectiveIsActive}
      feature={FeatureTypes.SHARES}
      title={modalTitle}
      titleIcon={<LuGitPullRequestArrow size={48} />}
      onGoBack={shareClass ? () => setShareClass(null) : null}
      subtitle={modalSubtitle}
      onClose={() => {
        reset()
        setShowContactSelectModal(false)
        setGeneralError('')
        setAlreadyWarned(false)
        setWarningAssets([])
        effectiveOnClose(false)
      }}
    >
      <Container>
        <FormProvider {...formMethods}>
          <form
            key={getModalKey('share-form-')}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            role="form"
          >
            {/*
              One summary for the whole form, listing every error at once. Rows still show their
              own errors inline; this repeats them so the reader can see what is wrong without
              hunting down a row that has scrolled out of view.
            */}
            {showErrorSummary && (
              <div className={styles.errorSummary} data-testid="share-form-error" role="alert">
                <p className={styles.errorSummaryTitle}>
                  {formErrors.length > 1 ? (
                    <FormattedMessage
                      {...messages.formErrorSummaryTitle}
                      values={{ count: formErrors.length }}
                    />
                  ) : (
                    formErrors[0]?.message || <FormattedMessage {...messages.formGeneralError} />
                  )}
                </p>
                {formErrors.length > 1 && (
                  <ul className={styles.errorSummaryList}>
                    {formErrors.map(({ key, row, message }) => (
                      <li key={key} data-testid={`share-form-error-item-${key}`}>
                        {row && <span className={styles.errorSummaryRow}>{`#${row} — `}</span>}
                        {message}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {warningAssets.length > 0 && (
              <WarningBox>
                <strong>Note:</strong> The share period of each of the following Smart Shares
                overlaps with that of an existing one for the same Asset and different Organization:{' '}
                {<strong>{warningAssets.map((wa) => `#${wa}`).join(', ')}</strong>}
              </WarningBox>
            )}
            <RequiredFieldsLabel>* Indicates a required field</RequiredFieldsLabel>

            <SelectContact
              key={getModalKey('select-contact-')}
              shareToExtend={effectiveShareToExtend}
              onwardShareToExtend={effectiveOnwardShareToExtend}
            />
            <AssetsTable
              key={getModalKey('assets-table-')}
              assetDefaultValues={assetDefaultValues}
              shareToExtend={effectiveShareToExtend}
              onwardShareToExtend={effectiveOnwardShareToExtend}
              onAddNewAsset={effectiveOnAddNewAsset}
            />
            <Actions>
              <Button
                type="submit"
                isLoading={isSubmitting}
                variant="secondary"
                $fitToContent
                size="large"
                minWidth
                disabled={Object.keys(formState.errors).length > 0 || isSubmitting}
                title={
                  Object.keys(formState.errors).length > 0 ? 'Please correct the form' : undefined
                }
              >
                <FormattedMessage {...messages.sendButton} />
              </Button>
            </Actions>
          </form>
        </FormProvider>
      </Container>
    </FeatureModal>
  )
}
